import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AccessScreen } from './AccessScreen';
import { WelcomeScreen } from './WelcomeScreen';
import { QuizFlow, TOTAL_STEPS } from './QuizFlow';
import { SummaryScreen } from './SummaryScreen';
import { ProcessingScreen } from './ProcessingScreen';
import { FinalScreen } from './FinalScreen';
import { PreparingOverlay } from '../components/PreparingOverlay';
import { LanguageProvider, useCopy } from '../contexts/LanguageContext';
import { emptyProfile } from '../types/profile';
import type { PersonalizationProfile } from '../types/profile';
import type { Language } from '../types/copy';
import { OTHER_INTEREST } from '../data/quizOptions';
import { submitPersonalizationProfile } from '../utils/submitProfile';
type Stage = 'access' | 'welcome' | 'quiz' | 'summary' | 'processing' | 'final' | 'closed';
interface PersonalizationFlowProps {
  language: Language;
}
export function PersonalizationFlow({
  language
}: PersonalizationFlowProps) {
  return <LanguageProvider language={language} data-id="element-76" data-mp="23fh8zqhn1s35">
      <FlowScreens language={language} data-id="element-77" data-mp="28c7oablmcjni" />
    </LanguageProvider>;
}
function FlowScreens({
  language
}: PersonalizationFlowProps) {
  const copy = useCopy();
  const [stage, setStage] = useState<Stage>('access');
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState<PersonalizationProfile>(emptyProfile);
  const [customInterest, setCustomInterest] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendDone, setSendDone] = useState(false);
  const [animationDone, setAnimationDone] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [stage, step]);

  // Solo pasamos a la pantalla final cuando el envío terminó
  // y la animación de procesamiento completó su secuencia.
  useEffect(() => {
    if (stage === 'processing' && sendDone && animationDone) {
      setStage('final');
    }
  }, [stage, sendDone, animationDone]);
  const update = useCallback((patch: Partial<PersonalizationProfile>) => {
    setProfile(previous => ({
      ...previous,
      ...patch
    }));
  }, []);
  const handleAnimationDone = useCallback(() => setAnimationDone(true), []);
  const handlePreparingDone = useCallback(() => {
    setPreparing(false);
    setStage('welcome');
  }, []);
  function handleNext() {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
      return;
    }
    setStage('summary');
  }
  function handleBack() {
    if (step > 1) {
      setStep(step - 1);
      return;
    }
    setStage('welcome');
  }
  async function handleSubmit() {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    setSendDone(false);
    setAnimationDone(false);
    const interests = profile.child_interests.map(key => key === OTHER_INTEREST && customInterest.trim() ? `${OTHER_INTEREST}: ${customInterest.trim()}` : key);

    // Mostramos la animación de procesamiento mientras viaja el POST.
    setStage('processing');
    try {
      await submitPersonalizationProfile({
        ...profile,
        child_interests: interests,
        parental_consent: true
      }, language);
      setSendDone(true);
    } catch (submitError) {
      // Ante un fallo de red volvemos al resumen conservando todas las respuestas.
      setStage('summary');
      setSubmitting(false);
      setError(submitError instanceof Error && submitError.message ? submitError.message : copy.summary.networkError);
    }
  }
  const parentFirstName = profile.parent_name.trim().split(' ')[0] || copy.closed.fallbackName;
  return <main className="relative flex min-h-screen w-full flex-col overflow-hidden bg-cream" style={{
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)'
  }} data-id="element-78" data-mp="1s6x3lblerej0">
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-lavender-100 blur-3xl" data-id="element-79" data-mp="2ljf0879drzm5" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-28 top-1/3 h-72 w-72 rounded-full bg-gold-200/40 blur-3xl" data-id="element-80" data-mp="2gktz85mb1bwn" />

      <div className="relative flex min-h-screen w-full flex-col" data-id="element-81" data-mp="26v0n93i92urv">
        <AnimatePresence mode="wait" data-id="element-82" data-mp="26so7mwzyswhz">
          {stage === 'access' && <AccessScreen key="access" profile={profile} update={update} onSubmit={() => setPreparing(true)} data-id="element-83" data-mp="21wz1ubxcqjd8" />}

          {stage === 'welcome' && <WelcomeScreen key="welcome" parentName={profile.parent_name} onStart={() => {
          setStep(1);
          setStage('quiz');
        }} data-id="element-84" data-mp="1swkfd98niy85" />}

          {stage === 'quiz' && <QuizFlow key="quiz" step={step} profile={profile} update={update} customInterest={customInterest} onCustomInterestChange={setCustomInterest} onNext={handleNext} onBack={handleBack} data-id="element-85" data-mp="3iagwy1vgymgy" />}

          {stage === 'summary' && <SummaryScreen key="summary" profile={profile} customInterest={customInterest} submitting={submitting} error={error} onConsentChange={value => update({
          parental_consent: value
        })} onSubmit={handleSubmit} onEdit={() => {
          setStep(1);
          setStage('quiz');
        }} data-id="element-86" data-mp="8bnforiafic3" />}

          {stage === 'processing' && <ProcessingScreen key="processing" childName={profile.child_name} onDone={handleAnimationDone} data-id="element-87" data-mp="w32ngzsu2ruq" />}

          {stage === 'final' && <FinalScreen key="final" childName={profile.child_name} email={profile.purchase_email} onFinish={() => setStage('closed')} data-id="element-88" data-mp="2x3fymkgqpk84" />}

          {stage === 'closed' && <motion.section key="closed" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-89" data-mp="323ltsam7xouy">
              <span aria-hidden="true" className="text-4xl" data-id="element-90" data-mp="2n6us1up2o126">
                💜
              </span>
              <h1 className="mt-4 font-display text-2xl font-semibold text-ink" data-id="element-91" data-mp="2if2xl92spb6r">
                {copy.closed.heading(parentFirstName)}
              </h1>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-soft" data-id="element-92" data-mp="1unxjayci6qyk">
                {copy.closed.text(profile.child_name, profile.purchase_email)}
              </p>
            </motion.section>}
        </AnimatePresence>

        <AnimatePresence data-id="element-93" data-mp="1lbqczuthbonc">
          {preparing && <PreparingOverlay key="preparing" onDone={handlePreparingDone} data-id="element-94" data-mp="1lcrpm3q58j76" />}
        </AnimatePresence>
      </div>
    </main>;
}